import React, { useState, useEffect, useCallback } from 'react';
import { MdEdit, MdDelete } from 'react-icons/md';
import { FiCpu, FiPlus, FiToggleLeft, FiToggleRight, FiX } from 'react-icons/fi';
import Layout from '../components/Layout';
import Modal from '../components/Modal';
import Toast, { ToastType } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { apiService } from '../services/api';

interface AIModel {
  id: number;
  provider: string;
  available_models: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const AISettings: React.FC = () => {
  const [aiModels, setAiModels] = useState<AIModel[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingModel, setEditingModel] = useState<AIModel | null>(null);
  const [modalData, setModalData] = useState({
    provider: '',
    models: [] as string[],
    is_active: true
  });
  const [newModel, setNewModel] = useState('');

  // Toast and confirm states
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  // Format date
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return 'Invalid date';
    }
  };

  const loadAiModels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getAIModels();
      if (response.success) {
        setAiModels(response.data);
      } else {
        setToast({ message: 'Failed to load AI models', type: 'error' });
      }
    } catch (error) {
      console.error('Error loading AI models:', error);
      setToast({ message: 'Failed to load AI models', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAiModels();
  }, [loadAiModels]);


  const openCreateModal = () => {
    setEditingModel(null);
    setModalData({
      provider: '',
      models: [],
      is_active: true
    });
    setNewModel('');
    setShowModal(true);
  };

  const openEditModal = (model: AIModel) => {
    setEditingModel(model);
    setModalData({
      provider: model.provider,
      models: model.available_models,
      is_active: model.is_active
    });
    setNewModel('');
    setShowModal(true);
  };

  const addModel = () => {
    if (newModel.trim() && !modalData.models.includes(newModel.trim())) {
      setModalData({
        ...modalData,
        models: [...modalData.models, newModel.trim()]
      });
      setNewModel('');
    }
  };

  const removeModel = (modelToRemove: string) => {
    setModalData({
      ...modalData,
      models: modalData.models.filter(model => model !== modelToRemove)
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addModel();
    }
  };

  const handleModalSave = async () => {
    try {      
      if (!modalData.provider.trim()) {
        setToast({ message: 'Provider name is required', type: 'error' });
        return;
      }

      if (modalData.models.length === 0) {
        setToast({ message: 'At least one model is required', type: 'error' });
        return;
      }

      let response;
      if (editingModel) {
        response = await apiService.updateAIModel(editingModel.id, {
          provider: modalData.provider.trim(),
          available_models: modalData.models,
          is_active: modalData.is_active
        });
      } else {
        const createData = {
          provider: modalData.provider.trim(),
          available_models: modalData.models,
          is_active: modalData.is_active
        };
        response = await apiService.createAIModel(createData);
      }

      if (response.success) {
        setToast({
          message: editingModel ? 'AI provider updated successfully' : 'AI provider created successfully',
          type: 'success'
        });
        setShowModal(false);
        loadAiModels();
      } else {
        setToast({ message: response.message || 'Operation failed', type: 'error' });
      }
    } catch (error: any) {
      console.error('Error saving AI model:', error);
      setToast({ message: error.response?.data?.message || 'Failed to save AI provider', type: 'error' });
    }
  };

  const handleToggleStatus = async (model: AIModel) => {
    try {
      const response = await apiService.updateAIModel(model.id, { is_active: !model.is_active });
      if (response.success) {
        setToast({
          message: `AI provider ${!model.is_active ? 'activated' : 'deactivated'} successfully`,
          type: 'success'
        });
        loadAiModels();
      } else {
        setToast({ message: 'Failed to update AI provider status', type: 'error' });
      }
    } catch (error) {
      console.error('Error toggling AI model status:', error);
      setToast({ message: 'Failed to update AI provider status', type: 'error' });
    }
  };

  const handleDelete = (model: AIModel) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete AI Provider',
      message: `Are you sure you want to delete the ${model.provider} AI provider? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const response = await apiService.deleteAIModel(model.id);
          if (response.success) {
            setToast({ message: 'AI provider deleted successfully', type: 'success' });
            loadAiModels();
          } else {
            setToast({ message: response.message || 'Failed to delete AI provider', type: 'error' });
          }
        } catch (error: any) {
          console.error('Error deleting AI model:', error);
          setToast({ message: error.response?.data?.message || 'Failed to delete AI provider', type: 'error' });
        }
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      }
    });
  };


  return (
    <Layout>
      <div className="mb-8">
        <div className="flex items-center">
          <FiCpu className="mr-3 text-2xl text-brand-500" />
          <div>
            <h1 className="text-2xl font-bold text-navy-700 dark:text-white">AI Settings</h1>  
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="mb-6 flex justify-end">
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white transition-colors hover:bg-brand-600"
        >
          <FiPlus className="h-4 w-4" />
          Add AI Provider
        </button>
      </div>

      {/* AI Models Table */}
      <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-navy-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-navy-600">
          <thead className="bg-gray-50 dark:bg-navy-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Provider
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Available Models
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-navy-600 dark:bg-navy-700">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center">
                  <div className="flex justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-500"></div>
                  </div>
                </td>
              </tr>
            ) : aiModels.length > 0 ? (
              aiModels.map((model) => (
                <tr key={model.id} className="hover:bg-gray-50 dark:hover:bg-navy-600">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {model.provider}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {model.available_models.map((modelName, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-800 dark:text-blue-100"
                        >
                          {modelName}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        model.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
                      }`}
                    >
                      {model.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(model.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleStatus(model)}
                        className={`rounded p-1 transition-colors ${
                          model.is_active
                            ? 'text-green-600 hover:bg-green-100 dark:hover:bg-green-800'
                            : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                        }`}
                        title={model.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {model.is_active ? <FiToggleRight className="h-5 w-5" /> : <FiToggleLeft className="h-5 w-5" />}
                      </button>
                      <button
                        onClick={() => openEditModal(model)}
                        className="rounded p-1 text-indigo-600 transition-colors hover:bg-indigo-100 dark:hover:bg-indigo-800"
                        title="Edit"
                      >
                        <MdEdit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(model)}
                        className="rounded p-1 text-red-600 transition-colors hover:bg-red-100 dark:hover:bg-red-800"
                        title="Delete"
                      >
                        <MdDelete className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                  No AI providers configured yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingModel ? 'Edit AI Provider' : 'Add AI Provider'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Provider Name
            </label>
            <input
              type="text"
              value={modalData.provider}
              onChange={(e) => setModalData({ ...modalData, provider: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
              placeholder="e.g., OpenAI, Anthropic, Gemini"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Available Models
            </label>
            
            {/* Add new model input */}
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newModel}
                onChange={(e) => setNewModel(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                placeholder="Enter model name (e.g., gpt-4)"
              />
              <button
                type="button"
                onClick={addModel}
                disabled={!newModel.trim()}
                className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                <FiPlus className="h-4 w-4" />
              </button>
            </div>

            {/* Model list */}
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {modalData.models.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm italic">No models added yet</p>
              ) : (
                modalData.models.map((model, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-gray-50 dark:bg-navy-800 rounded-lg px-3 py-2"
                  >
                    <span className="text-gray-700 dark:text-gray-300">{model}</span>
                    <button
                      type="button"
                      onClick={() => removeModel(model)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <FiX className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={modalData.is_active}
              onChange={(e) => setModalData({ ...modalData, is_active: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Active
            </label>
          </div>

          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
            <button
              onClick={() => setShowModal(false)}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleModalSave}
              className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors"
            >
              {editingModel ? 'Update Provider' : 'Create Provider'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Delete"
        type="danger"
      />

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </Layout>
  );
};

export default AISettings;
